import { supabase } from './supabase';

export interface InsurancePolicy {
  id: string;
  patient_id: string;
  policy_number: string;
  insurance_company: string;
  policy_type: 'individual' | 'family' | 'group' | 'government';
  coverage_amount: number;
  deductible: number;
  copay_percentage: number;
  effective_date: string;
  expiry_date: string;
  status: 'active' | 'inactive' | 'expired' | 'cancelled';
  created_at: string;
  updated_at: string;
}

export interface InsuranceClaim {
  id: string;
  policy_id: string;
  invoice_id?: string;
  admission_id?: string;
  preauth_id?: string;
  claim_number: string;
  claim_date: string;
  treatment_date: string;
  diagnosis_code?: string;
  treatment_code?: string;
  billed_amount: number;
  approved_amount?: number;
  paid_amount?: number;
  status: 'draft' | 'submitted' | 'under_review' | 'approved' | 'partially_approved' | 'denied' | 'paid';
  denial_reason?: string;
  processed_date?: string;
  payment_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const insurancePolicyService = {
  async getByPatient(patientId: string): Promise<InsurancePolicy[]> {
    const { data, error } = await supabase
      .from('insurance_policies')
      .select('*')
      .eq('patient_id', patientId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getActive(patientId: string): Promise<InsurancePolicy | null> {
    const { data, error } = await supabase
      .from('insurance_policies')
      .select('*')
      .eq('patient_id', patientId)
      .eq('status', 'active')
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(policy: Omit<InsurancePolicy, 'id' | 'created_at' | 'updated_at'>): Promise<InsurancePolicy> {
    const { data, error } = await supabase
      .from('insurance_policies')
      .insert(policy)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },
};

export const insuranceClaimService = {
  async getAll(): Promise<InsuranceClaim[]> {
    const { data, error } = await supabase
      .from('insurance_claims')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<InsuranceClaim | null> {
    const { data, error } = await supabase
      .from('insurance_claims')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  },

  async create(claim: Omit<InsuranceClaim, 'id' | 'claim_number' | 'created_at' | 'updated_at'>): Promise<InsuranceClaim> {
    const claimNumber = `CLM-${Date.now()}`;
    
    const { data, error } = await supabase
      .from('insurance_claims')
      .insert({
        ...claim,
        claim_number: claimNumber,
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateStatus(id: string, status: InsuranceClaim['status'], approvedAmount?: number): Promise<InsuranceClaim> {
    const updateData: any = { 
      status,
      updated_at: new Date().toISOString()
    };
    
    if (approvedAmount !== undefined) {
      updateData.approved_amount = approvedAmount;
    }
    
    if (status === 'approved' || status === 'partially_approved') {
      updateData.processed_date = new Date().toISOString().split('T')[0];
    }
    
    if (status === 'paid') {
      updateData.payment_date = new Date().toISOString().split('T')[0];
      updateData.paid_amount = approvedAmount || updateData.approved_amount;
    }
    
    const { data, error } = await supabase
      .from('insurance_claims')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async uploadDocument(claimId: string, file: File): Promise<string> {
    const fileName = `claim-${claimId}-${Date.now()}-${file.name}`;
    const { data, error } = await supabase.storage
      .from('insurance-documents')
      .upload(fileName, file);
    
    if (error) throw error;
    return fileName;
  },
};
